import { useNavigate, Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import HowItWorks from "@/components/sections/HowItWorks";
import FinalCTA from "@/components/sections/FinalCTA";
import { Check, ArrowRight, Download } from "lucide-react";

export default function AudiencePage({ testId, overline, title, subtitle, image, pains, gains, accentDark, extraCta }) {
  const navigate = useNavigate();
  return (
    <div data-testid={testId}>
      <section className={`pt-32 pb-20 md:pt-40 ${accentDark ? "bg-[#121316] text-white" : "bg-white"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className={`text-xs font-semibold tracking-[0.2em] uppercase font-mono ${accentDark ? "text-blue-300" : "text-[#0033A0]"}`}>
              {overline}
            </p>
            <h1 className={`mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] ${accentDark ? "text-white" : "text-zinc-900"}`}>
              {title}
            </h1>
            <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${accentDark ? "text-zinc-400" : "text-zinc-600"}`}>
              {subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                data-testid={`${testId}-demo-button`}
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center gap-2 bg-[#0033A0] text-white hover:bg-[#00257A] transition-colors px-7 py-3.5 text-sm font-medium rounded-sm"
              >
                Book a demo <ArrowRight size={16} />
              </button>
              {extraCta && (
                <Link
                  to={extraCta.href}
                  data-testid="audience-extra-cta"
                  className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-medium rounded-sm border transition-colors ${
                    accentDark
                      ? "border-zinc-700 text-white hover:bg-white/5"
                      : "border-zinc-300 text-zinc-900 hover:bg-zinc-50"
                  }`}
                >
                  <Download size={16} /> {extraCta.label}
                </Link>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <img
              src={image}
              alt={overline}
              className="rounded-2xl border border-zinc-200 w-full h-72 object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <Reveal className="rounded-2xl border border-zinc-200 bg-white p-8 md:p-10">
            <h2 className="font-heading text-xl font-medium text-zinc-900">The pains we remove</h2>
            <ul className="mt-6 space-y-4">
              {pains.map((p) => (
                <li key={p} className="text-sm text-zinc-600 leading-relaxed border-l-2 border-zinc-200 pl-4">{p}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="rounded-2xl bg-[#0033A0] p-8 md:p-10 text-white">
            <h2 className="font-heading text-xl font-medium">What you gain</h2>
            <ul className="mt-6 space-y-4">
              {gains.map((g) => (
                <li key={g} className="flex items-start gap-3 text-sm text-blue-50">
                  <Check size={16} className="shrink-0 mt-0.5" /> {g}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <HowItWorks />
      <FinalCTA />
    </div>
  );
}
